import { useStore } from 'react-redux'
import {
  checkoutSelector,
  isRecoverableFlow,
  recoverableTerminalErrorsStateSelector,
  startedCheckoutSelector,
  useAppRouter,
  useAppSelector,
  useConsumerSelector,
  useIsBillingAgreementApprovalProcess,
  usePreferredPaymentMethod,
  useSelectedPaymentType,
} from '..'
import { useIsApplePaylater } from '../applePaylater/hooks'
import { useFlag } from '@afterpaytouch/feature'
import { Features, isOverdueResponseType, PaymentMethodType } from '../../model'
import { CheckoutAppSelector } from '../store'
import { ApplePayHelper } from './helper'
import { ApplePayState } from './reducer'
import { PaymentType } from '@afterpay/types'
import { FirstPaymentType } from '@afterpaytouch/portal-api'
import { createSelector } from '@reduxjs/toolkit'
import { differenceInDays } from 'date-fns'

export interface LineItem {
  amount: string
  count: number
  startDate?: Date
  endDate?: Date
  paymentIntervalCount?: number
}

interface TotalLineItem {
  amount: string
  date?: string
  puf: boolean
  currencyCode: string
}

// @ts-expect-error: OPERATION BLEED STOPPER
export const applePayTotalLineItem: CheckoutAppSelector<TotalLineItem> = (state) => {
  const { amountDueToday, paymentSchedules, paymentUpFront, promotion } = startedCheckoutSelector(state) ?? {}
  const { status: recoverableFlowStatus, selectedOrder } = recoverableTerminalErrorsStateSelector(state)

  if (isRecoverableFlow(recoverableFlowStatus)) {
    if (isOverdueResponseType(selectedOrder)) {
      return {
        amount: selectedOrder?.totalAmount.amount,
        currencyCode: selectedOrder?.totalAmount.currency,
        date: selectedOrder?.overduePaymentSchedules[0].installmentDueDate,
        puf: false,
      }
    }

    return {
      // @ts-expect-error: OPERATION BLEED STOPPER
      amount: selectedOrder.orderSummary.totalOwed.amount,
      // @ts-expect-error: OPERATION BLEED STOPPER
      currencyCode: selectedOrder.orderSummary.totalOwed.currency,
      puf: false,
    }
  }

  return {
    // @ts-expect-error: OPERATION BLEED STOPPER
    amount: promotion != null && paymentUpFront ? promotion.orderDiscountedPaymentSchedules[0].owedAfterDiscount?.amount : amountDueToday.amount,
    // @ts-expect-error: OPERATION BLEED STOPPER
    currencyCode: amountDueToday.currency,
    // @ts-expect-error: OPERATION BLEED STOPPER
    date: paymentSchedules.length > 0 ? paymentSchedules[0].installmentDueDate : undefined,
    puf: paymentUpFront,
  }
}

// @ts-expect-error: OPERATION BLEED STOPPER
export const applePayLineItems: CheckoutAppSelector<LineItem[]> = (state) => {
  const firstPaymentType = checkoutSelector(state)?.firstPaymentType ?? FirstPaymentType.PaymentUpfront
  const { amountDueToday, paymentSchedules, promotion, paymentUpFront } = startedCheckoutSelector(state) ?? {}
  const { status: recoverableFlowStatus, selectedOrder } = recoverableTerminalErrorsStateSelector(state)

  if (isRecoverableFlow(recoverableFlowStatus)) {
    if (isOverdueResponseType(selectedOrder)) {
      const startOverdueDate = new Date(selectedOrder?.overduePaymentSchedules[0].installmentDueDate)
      const secondLastOverdueDate = new Date(selectedOrder?.overduePaymentSchedules[selectedOrder?.overduePaymentSchedules.length - 2].installmentDueDate)
      const endOverdueDate = new Date(selectedOrder?.overduePaymentSchedules[selectedOrder?.overduePaymentSchedules.length - 1].installmentDueDate)
      const recurringPaymentIntervalCount = differenceInDays(endOverdueDate, secondLastOverdueDate)

      return [
        {
          amount: selectedOrder?.overduePaymentSchedules[0].amount.amount,
          count: selectedOrder?.overduePaymentSchedules.length,
          startDate: startOverdueDate,
          endDate: endOverdueDate,
          paymentIntervalCount: recurringPaymentIntervalCount,
        },
      ]
    }

    return [
      {
        amount: selectedOrder?.orderSummary.totalOwed.amount,
        count: 1,
      },
    ]
  }

  let promoDiscount = 0
  if (promotion != null && paymentUpFront) {
    // @ts-expect-error: OPERATION BLEED STOPPER
    promoDiscount = parseFloat(promotion.orderDiscountedPaymentSchedules[0].discount.amount)
  }

  const endPaymentDate = getDueDateAsDate(paymentSchedules, null, 1)
  const secondLastPaymentDate = getDueDateAsDate(paymentSchedules, null, 2)
  const recurringPaymentIntervalCount = differenceInDays(endPaymentDate, secondLastPaymentDate)

  if (firstPaymentType === FirstPaymentType.LargePaymentUpfront || firstPaymentType === FirstPaymentType.VariablePaymentUpfront) {
    return [
      // @ts-expect-error: OPERATION BLEED STOPPER
      { amount: (parseFloat(amountDueToday.amount) - promoDiscount).toFixed(2), count: 1 },
      {
        // @ts-expect-error: OPERATION BLEED STOPPER
        amount: paymentSchedules.length > 0 ? (parseFloat(paymentSchedules[1].amount.amount) - promoDiscount).toFixed(2) : '0.00',
        // @ts-expect-error: OPERATION BLEED STOPPER
        count: paymentSchedules.length - 1,
        startDate: getDueDateAsDate(paymentSchedules, 1, null),
        endDate: endPaymentDate,
        paymentIntervalCount: recurringPaymentIntervalCount,
      },
    ]
  }
  return [
    {
      // @ts-expect-error: OPERATION BLEED STOPPER
      amount: paymentSchedules.length > 0 ? (parseFloat(paymentSchedules[0].amount.amount) - promoDiscount).toFixed(2) : '0.00',
      // @ts-expect-error: OPERATION BLEED STOPPER
      count: paymentSchedules.length,
      startDate: getDueDateAsDate(paymentSchedules, 0, null),
      endDate: endPaymentDate,
      paymentIntervalCount: recurringPaymentIntervalCount,
    },
  ]
}

export const applePayBillingAgreementLineItem: CheckoutAppSelector<LineItem[]> = (state) => {
  const { paymentSchedules } = startedCheckoutSelector(state) ?? {}

  return [
    {
      amount: paymentSchedules !== undefined && paymentSchedules.length > 0 ? parseFloat(paymentSchedules[0].amount.amount).toFixed(2) : '0.00',
      count: paymentSchedules?.length ?? 0,
      startDate: getDueDateAsDate(paymentSchedules, 0, null),
    },
  ]
}

const getDueDateAsDate = (paymentSchedules, dueDateIndex, dueDateIndexFromLength): Date => {
  if (paymentSchedules.length === 0) {
    return new Date()
  }

  const dateIndex = dueDateIndex !== undefined && dueDateIndex !== null ? dueDateIndex : paymentSchedules.length - dueDateIndexFromLength
  const dueDate = paymentSchedules[dateIndex]?.installmentDueDate ?? null
  return dueDate === null ? new Date() : new Date(dueDate)
}

export const applePaySelector: CheckoutAppSelector<ApplePayState> = (state) => state.applepay

export const applePayMPANFlowSelector: CheckoutAppSelector<boolean> = createSelector(applePaySelector, (state) => state.isEnabledForMPANFlow ?? false)

export const useApplePayState = (): ApplePayState => useAppSelector(applePaySelector)

export const useApplePayFeature = (): {
  new: boolean
  global: boolean
  enabledForRecurringPayments: boolean
  enabledForConsumerLending: boolean
} => {
  const isEnabledNew = useFlag(Features.ENABLE_APPLE_PAY_FOR_NEW_CHECKOUT)
  const isEnabledGlobal = useFlag(Features.ROSELLA_PAYMENT_IN_CHECKOUT)
  const isBillingAgreementApprovalProcess = useIsBillingAgreementApprovalProcess()
  const isEnabledForCL = useFlag(Features.ENABLE_APPLE_PAY_FOR_CONSUMER_LENDING)
  const selectedPaymentType = useSelectedPaymentType() ?? PaymentType.PBI

  return {
    new: isEnabledNew,
    global: isEnabledGlobal,
    enabledForRecurringPayments: !isBillingAgreementApprovalProcess,
    enabledForConsumerLending: selectedPaymentType === PaymentType.PBI || isEnabledForCL,
  }
}

export const useApplePayPayment = (): boolean => {
  const isApplePayEnabled = useApplePayFeature()
  const { isEnabledForDevice } = useApplePayState()
  const { preferredPaymentMethod } = useConsumerSelector()
  const isPreferredMethod = preferredPaymentMethod === PaymentMethodType.APPLE_PAY

  return (
    isApplePayEnabled.new &&
    isEnabledForDevice &&
    (isPreferredMethod || isApplePayEnabled.global) &&
    isApplePayEnabled.enabledForRecurringPayments &&
    isApplePayEnabled.enabledForConsumerLending
  )
}

export const useShouldSetApplePayAsPurchaseMethod = (): boolean => {
  const preferredPaymentMethod = usePreferredPaymentMethod()
  const isApplePayEnabled = useApplePayFeature()
  const { isEnabledForDevice } = useApplePayState()
  const isApplePaylater = useIsApplePaylater()

  if (isApplePaylater) {
    return false
  }

  return preferredPaymentMethod === PaymentMethodType.APPLE_PAY && isApplePayEnabled.new && isEnabledForDevice && isApplePayEnabled.enabledForRecurringPayments
}

export const useApplePay = (): ApplePayHelper => {
  const store = useStore()
  const { locale } = useAppRouter()

  return ApplePayHelper.getInstance({ store, locale })
}
